import { useMemo, useState } from 'react';
import { App, Button, Card, Checkbox, Input } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customerSmsApi } from '@/core/api/customerSmsApi';
import { getErrorMessage } from '@/core/utils/errors';
import { AppIcon } from '@/shared/components/AppIcon';

const fa = (value: number): string => new Intl.NumberFormat('fa-IR').format(value);

export const CustomerSmsPage = (): React.ReactElement => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [text, setText] = useState('');
  const recipients = useQuery({ queryKey: ['customer-sms', 'recipients', search], queryFn: () => customerSmsApi.recipients(search) });

  const send = useMutation({
    mutationFn: () => customerSmsApi.send(selected, text),
    onSuccess: (result) => {
      message[result.failed ? 'warning' : 'success'](`${fa(result.sent)} پیام ارسال شد${result.failed ? ` و ${fa(result.failed)} ارسال ناموفق بود.` : '.'}`);
      void queryClient.invalidateQueries({ queryKey: ['tenant-workspace', 'subscription'] });
    },
    onError: (error) => message.error(getErrorMessage(error)),
  });

  const availableIds = useMemo(() => (recipients.data ?? []).map((x) => x.contactId), [recipients.data]);
  const allSelected = availableIds.length > 0 && availableIds.every((id) => selected.includes(id));
  return (
    <div className="space-y-6">
      <section>
        <p className="text-sm font-bold text-brand-600">ارتباط مستقیم با مشتری</p>
        <h1 className="mt-1 text-2xl font-black text-ink-950 sm:text-3xl">ارسال پیام به مشتریان</h1>
        <p className="mt-2 text-sm leading-7 text-slate-500">پیام موردنظر را برای یک یا چند مشتری انتخاب‌شده ارسال کنید.</p>
      </section>

      <div className="grid gap-6 xl:grid-cols-[1.15fr_.85fr]">
        <Card title="انتخاب مشتریان" className="rounded-3xl">
          <div className="flex flex-col gap-3 sm:flex-row">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="جست‌وجوی نام یا موبایل مشتری" prefix={<AppIcon name="search" size={16} />} />
            <Button onClick={() => setSelected(allSelected ? selected.filter((id) => !availableIds.includes(id)) : Array.from(new Set([...selected, ...availableIds])))} disabled={!availableIds.length}>{allSelected ? 'لغو انتخاب همه' : 'انتخاب همه نتایج'}</Button>
          </div>
          <div className="mt-4 max-h-[430px] space-y-2 overflow-y-auto pl-1">
            {recipients.isLoading ? <p className="py-8 text-center text-sm text-slate-400">در حال دریافت مشتریان…</p> : (recipients.data ?? []).length ? (recipients.data ?? []).map((item) => (
              <label key={item.contactId} className="flex cursor-pointer items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-slate-50/70 p-3 hover:border-brand-200">
                <div className="min-w-0"><p className="truncate text-sm font-black text-slate-700">{item.displayName}</p><p className="mt-1 font-sans text-xs text-slate-400" dir="ltr">{item.mobile}</p></div>
                <Checkbox checked={selected.includes(item.contactId)} onChange={(e) => setSelected((current) => e.target.checked ? [...new Set([...current, item.contactId])] : current.filter((id) => id !== item.contactId))} />
              </label>
            )) : <p className="py-8 text-center text-sm text-slate-400">مشتری فعال دارای شماره موبایل یافت نشد.</p>}
          </div>
        </Card>

        <Card title="متن و ارسال" className="rounded-3xl">
          <div className="rounded-2xl bg-brand-50 p-4 text-sm font-bold text-brand-700">{fa(selected.length)} مشتری انتخاب شده است.</div>
          <Input.TextArea className="mt-4" rows={9} maxLength={2000} showCount value={text} onChange={(e) => setText(e.target.value)} placeholder="متن پیام را وارد کنید…" />
          <Button className="mt-4 w-full" size="large" type="primary" icon={<AppIcon name="send" size={17} />} loading={send.isPending} disabled={!selected.length || !text.trim()} onClick={() => send.mutate()}>
            {selected.length <= 1 ? 'ارسال پیام' : `ارسال پیام به ${fa(selected.length)} مشتری`}
          </Button>
        </Card>
      </div>

    </div>
  );
};
